import React, { useEffect, useMemo } from 'react'
import { Box, Checkbox, Link, Skeleton, Typography } from '@material-ui/core'
import { Link as RouterLink } from 'react-router-dom'
import { PATH_DASHBOARD } from 'src/routes/paths'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import CandidateRow from './components/CandidateRow'
import DraftList from './components/DraftList'
import { GroupLabel, Muted, TextLink } from './components/ui'
import { TemplateChoices } from './SheetImport'
import { candidateFitsWorkflow, candidateInfo, isAwaitingAnalysis } from './model/candidates'
import { READY_WINDOW_MS } from './model/ready'
import { INPUT_LABEL, INPUT_OPTIONS } from './model/workflow'

// The samples area while the batch is empty. On top, the user's other drafts to
// continue (when there are any). Then the uploads waiting for an analysis of the
// chosen analysis and input, as a multi-select list: a click (or Enter or Space) on a
// row ticks it, "Select all" ticks every row shown, and the panel's footer adds the
// ticked rows ("Add 3 samples"). The panel owns the selection (`selected`, in the
// list's order, and onSelect). "Browse all uploads" opens the file picker. Files are
// uploaded on the Files page only, where they are paired and verified; a line under
// the list links there, and they join the list once verified. A sample sheet line
// closes it: the Excel and CSV templates, and the import when the panel offers it.

const RECENT_COUNT = 8

/**
 * The rows of "Uploaded, not analysed yet": uploads without an analysis that can be
 * analysed, the ready ones before those still verifying, newest first within each.
 * A sample still verifying after READY_WINDOW_DAYS is left out, as on Home's strip:
 * it is stuck rather than on its way. Failed and R1-only uploads are left to the
 * picker, which says why. With `workflow`, only the uploads that fit its analysis
 * and input (candidateFitsWorkflow).
 */
export const recentUploads = (
  candidates = [],
  analysed = new Set(),
  { now = Date.now(), limit = RECENT_COUNT, workflow } = {},
) => {
  const ready = []
  const verifying = []
  candidates.forEach((candidate) => {
    if (workflow && !candidateFitsWorkflow(candidate, workflow)) return
    if (!isAwaitingAnalysis(candidate, analysed, { now })) return
    const info = candidateInfo(candidate, { now })
    const entry = { candidate, uploadedAt: info.uploadedAt }
    if (info.state !== 'verifying') ready.push(entry)
    else if (info.uploadedAt > 0 && now - info.uploadedAt <= READY_WINDOW_MS) verifying.push(entry)
  })
  const newestFirst = (a, b) => b.uploadedAt - a.uploadedAt
  return [...ready.sort(newestFirst), ...verifying.sort(newestFirst)].slice(0, limit).map(({ candidate }) => candidate)
}

// A plain click on a link navigates in this tab; a modified or middle click opens
// another tab and leaves the panel as it is.
const navigatesHere = (event) =>
  event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)

// What the list is of: "PacBio BAM", or "SNV/Indel VCF" for a somatic run.
const inputLabelOf = (workflow) =>
  workflow?.analysis === 'somatic' && workflow?.input === 'vcf' ? 'SNV/Indel VCF' : INPUT_LABEL[workflow?.input] || ''

const EmptySamples = ({
  candidates = [],
  analysed,
  isLoading,
  workflow,
  waitingByInput = {},
  selected = [],
  onSelect,
  drafts = [],
  onContinueDraft,
  onDeleteDraft,
  onOpenPicker,
  onSwitchInput,
  onImportSheet,
  onLeave,
}) => {
  const scope = workflow ? `${workflow.analysis}:${workflow.input}` : ''
  const all = useMemo(
    () => recentUploads(candidates, analysed, { workflow, limit: Infinity }),
    // Only the analysis and input scope the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates, analysed, scope],
  )
  const recent = useMemo(() => all.slice(0, RECENT_COUNT), [all])
  const selectable = useMemo(() => recent.filter((c) => !candidateInfo(c).disabledReason), [recent])
  const selectedIds = new Set(selected.map((c) => c.id))

  // The selection follows the rows shown: a row that leaves the list (analysed
  // meanwhile, another input) leaves it too, and a refreshed row replaces its old self.
  useEffect(() => {
    if (!onSelect || !selected.length) return
    const ids = new Set(selected.map((c) => c.id))
    const next = selectable.filter((c) => ids.has(c.id))
    if (next.length !== selected.length || next.some((c, i) => c !== selected[i])) onSelect(next)
  }, [selectable, selected, onSelect])

  // Always in the list's order, so the batch reads as the list did.
  const choose = (ids) => onSelect && onSelect(selectable.filter((c) => ids.has(c.id)))
  const toggle = (candidate) => {
    const next = new Set(selectedIds)
    if (next.has(candidate.id)) next.delete(candidate.id)
    else next.add(candidate.id)
    choose(next)
  }
  const allSelected = selectable.length > 0 && selectable.every((c) => selectedIds.has(c.id))
  const someSelected = selectable.some((c) => selectedIds.has(c.id))
  const toggleAll = () => choose(allSelected ? new Set() : new Set(selectable.map((c) => c.id)))

  const label = inputLabelOf(workflow)
  const otherInputs = workflow
    ? (INPUT_OPTIONS[workflow.analysis] || [])
        .map((o) => ({ ...o, count: Number(waitingByInput[o.value]) || 0 }))
        .filter((o) => o.value !== workflow.input && o.count > 0)
    : []
  const waitingElsewhere = otherInputs.length > 0
  const showRows = !isLoading && recent.length > 0

  const list = (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, mb: 0.5, px: 1, minHeight: 36 }}>
        {showRows && (
          <Box sx={{ width: 36, flexShrink: 0, display: 'flex' }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              disabled={!selectable.length}
              onChange={toggleAll}
              inputProps={{ 'aria-label': 'Select all' }}
              sx={{ p: 0.5 }}
            />
          </Box>
        )}
        <GroupLabel sx={{ mb: 0, flex: 1, minWidth: 0 }}>
          {label ? `${label} uploads not analysed yet` : 'Uploaded, not analysed yet'}
        </GroupLabel>
        <TextLink onClick={onOpenPicker}>
          {!isLoading && all.length > recent.length ? `Browse all ${all.length} uploads` : 'Browse all uploads'}
        </TextLink>
      </Box>
      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton key={i} variant="text" height={44} sx={{ mx: 1 }} />
        ))
      ) : showRows ? (
        recent.map((c) => (
          <CandidateRow key={c.id} candidate={c} selectable checked={selectedIds.has(c.id)} onToggle={toggle} />
        ))
      ) : (
        <Box sx={{ px: 1, pt: 2, pb: 0.5 }}>
          {waitingElsewhere ? (
            <Muted>{label ? `No ${label} uploads waiting.` : 'No uploads waiting.'}</Muted>
          ) : (
            <Typography sx={{ fontSize: 14 }}>Nothing waiting to be analysed.</Typography>
          )}
        </Box>
      )}
      <Box sx={{ px: 1, mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {!isLoading && waitingElsewhere && (
          <Muted>
            Also waiting:{' '}
            {otherInputs.map((o, i) => (
              <React.Fragment key={o.value}>
                {i ? ', ' : ''}
                <TextLink onClick={() => onSwitchInput && onSwitchInput(o.value)}>
                  {o.count} {o.label}
                </TextLink>
              </React.Fragment>
            ))}
          </Muted>
        )}
        {/* Every upload goes through the Files page and its checks. Leaving for it
            closes the panel, as the X does (the work is saved to Drafts). */}
        <Muted>
          New files? Upload them on the{' '}
          <Link
            component={RouterLink}
            to={PATH_DASHBOARD.general.files}
            underline="hover"
            onClick={(event) => {
              if (onLeave && navigatesHere(event)) onLeave()
            }}
            sx={{ fontWeight: 500 }}
          >
            Files page
          </Link>
          . They appear here once verified.
        </Muted>
      </Box>
    </Box>
  )

  const sheet = (
    <Box sx={{ mt: 4, px: 1 }}>
      <GroupLabel>Sample sheet</GroupLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
        {onImportSheet && (
          <ResultButton onClick={onImportSheet} sx={{ height: 32, px: 1.5, fontSize: 13.5 }}>
            Import a sample sheet (Excel or CSV)
          </ResultButton>
        )}
        <TemplateChoices samples={[]} workflow={workflow} />
      </Box>
      <Muted sx={{ mt: 1 }}>
        {onImportSheet
          ? 'Rows bring in the matching uploads with their details.'
          : 'Import it once the samples are in the batch.'}
      </Muted>
    </Box>
  )

  return (
    <Box sx={{ maxWidth: 880 }}>
      <DraftList drafts={drafts} onContinue={onContinueDraft} onDelete={onDeleteDraft} sx={{ mb: 4 }} />
      {list}
      {workflow && sheet}
    </Box>
  )
}

export default EmptySamples
